import { AiOutlineArrowUp, AiOutlineArrowDown, AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from "@tanstack/react-table";
import { CityData } from "@/types/city";
import styles from "./css/CityManagementTable.module.css";
import { useDispatch } from "react-redux";
import { useDeleteCity } from "@/hooks/city/useDeleteCity";
import { setToast } from "@/store/systemSlice";
import { useTranslation } from 'react-i18next';
import GenericConfirmModal from "@/components/modals/GenericModal";

interface CityManagementTableProps {
  className?: string;
  data: CityData[];
  onDataChange: () => void;
  onRowClick?: (city: CityData) => void;
}

const CityManagementTable: React.FC<CityManagementTableProps> = ({ 
  className, 
  data, 
  onDataChange,
  onRowClick 
}) => {
  const { t } = useTranslation('SuperAdminCities');
  const [localData, setLocalData] = useState<CityData[]>(data);
  const [deletingCityId, setDeletingCityId] = useState<string>("");
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    cityId: string;
    cityName: string;
  }>({
    isOpen: false,
    cityId: "",
    cityName: "",
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const deleteCityMutation = useDeleteCity({
    onSuccess: (data) => {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: data.message,
          toastType: "success",
        })
      );
      setDeletingCityId("");
      onDataChange();
    },
    onError: (error: any) => {
      dispatch(
        setToast({
          toastMessage: "",
          toastTitle: error.response?.data?.message || t("deleteFailed"),
          toastType: "error",
        })
      );
      setDeletingCityId("");
    },
  });

  const handleDeleteClick = (cityId: string, cityName: string) => {
    setConfirmModal({
      isOpen: true,
      cityId,
      cityName,
    });
  };

  const handleConfirmDelete = () => {
    setDeletingCityId(confirmModal.cityId);
    deleteCityMutation.mutate({ city_id: confirmModal.cityId });
    setConfirmModal({ isOpen: false, cityId: "", cityName: "" });
  };

  const columns = useMemo<ColumnDef<CityData>[]>(
    () => [
      {
        accessorKey: "logo",
        header: "",
        cell: (info) => (
          <div className={styles.logoCell}>
            {info.getValue() ? (
              <img 
                src={info.getValue() as string} 
                alt="City logo"
              />
            ) : (
              <div className={styles.logoPlaceholder}>🏛️</div>
            )}
          </div>
        ),
        size: 80,
      },
      {
        accessorKey: "name",
        header: "שם העיר",
        cell: (info) => (
          <div className={styles.nameCell}>
            <strong>{info.getValue() as string}</strong>
          </div>
        ),
        size: 180,
      },
      {
        accessorKey: "name_en",
        header: "שם העיר באנגלית",
        cell: (info) => (
          <div className={styles.nameCell}>
            {info.getValue() as string || "-"}
          </div>
        ),
        size: 180,
      },
      {
        accessorKey: "projects",
        header: t("projectsCount"),
        cell: (info) => (
          <div className={styles.centerCell}>
            {info.getValue() as number}
          </div>
        ),
      },
      {
        accessorKey: "users_count",
        header: t("usersCount"),
        cell: (info) => (
          <div className={styles.centerCell}>
            {info.getValue() as number || 0}
          </div>
        ),
      },
      {
        accessorKey: "population",
        header: "אוכלוסייה",
        cell: (info) => (
          <div className={styles.centerCell}>
            {info.getValue() ? (info.getValue() as number).toLocaleString() : "-"}
          </div>
        ),
      },
      {
        accessorKey: "created_at",
        header: t("createdAt"),
        cell: (info) => {
          const dateStr = info.getValue() as string;
          if (!dateStr) return "-";
          const date = new Date(dateStr);
          return (
            <div className={styles.dateCell}>
              {date.toLocaleDateString('fr-FR')}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "פעולות",
        cell: (info) => {
          const city = info.row.original;
          const isDeleting = deletingCityId === city.id;
          
          return (
            <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
              <div 
                className={`${styles.iconButton} ${styles.editButton}`}
                onClick={() => onRowClick?.(city)}
                title={t("editCity")}
              >
                <AiOutlineEdit size={18} />
              </div>
              <div 
                className={`${styles.iconButton} ${styles.deleteButton} ${isDeleting ? styles.disabled : ''}`}
                onClick={() => !isDeleting && handleDeleteClick(city.id, city.name)}
                title={t("deleteCity")}
              >
                {isDeleting ? (
                  <div className={styles.spinner}>⏳</div>
                ) : (
                  <AiOutlineDelete size={18} />
                )}
              </div>
            </div>
          );
        },
        size: 120,
      },
    ],
    [t, deletingCityId, onRowClick]
  );

  const table = useReactTable({
    data: localData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className={`${styles.tableContainer} ${className || ""}`}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: header.getSize() }}
                    onClick={header.column.getToggleSortingHandler()}
                    className={header.column.getCanSort() ? styles.sortableHeader : ""}
                  >
                    <div className={styles.headerContent}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() && (
                        <span className={styles.sortIcon}>
                          {header.column.getIsSorted() === "asc" ? (
                            <AiOutlineArrowUp />
                          ) : (
                            <AiOutlineArrowDown />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                className={styles.tableRow}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} style={{ width: cell.column.getSize() }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <GenericConfirmModal
        isOpen={confirmModal.isOpen}
        title={t("confirmDeleteTitle")}
        description={t("confirmDeleteMessage", { cityName: confirmModal.cityName })}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmModal({ isOpen: false, cityId: "", cityName: "" })}
        loading={deleteCityMutation.isPending}
        type="delete"
      />
    </>
  );
};

export default CityManagementTable;
